(() => {
  const CONTROLLER_KEY = '__evaFeatureBandStepController';
  const VERSION = 14;

  const DEFAULTS = {duration: 600};

  const HYSTERESIS = 0.06;
  const SHELL_TAKEOVER_HYSTERESIS = 8;

  const deckQuery = window.matchMedia('(min-width: 750px)');
  {

    const existing = window[CONTROLLER_KEY];
    if (existing?.version === VERSION) {
      existing.refresh?.();
      return;
    }
    existing?.destroy?.();
  }

  let bands = [];
  let activationFrame = 0;
  let resizeFrame = 0;
  let geometryObserver = null;
  const observedGeometry = new WeakSet();
  const shellOwners = new Set();
  const shellElementState = new Map();

  function shellElements() {
    return [...new Set([
      ...document.querySelectorAll('header[data-mobile-sticky-menu-enabled]'),
      ...document.querySelectorAll('.centauri-eva-asuka .centauri-c01-product-bar-block--sticky'),
    ])];
  }

  function rescueShellFocus(entry) {
    const focused = document.activeElement;
    if (!focused || !shellElements().some((element) => element.contains(focused))) return;
    const stop = nearestStop(entry);
    const target = stop?.chapter ?? entry.band.querySelector('[data-eva-chapter]') ?? entry.band;
    target.setAttribute('tabindex', '-1');
    target.focus({preventScroll: true});
    if (document.activeElement === target) return;
    entry.band.setAttribute('tabindex', '-1');
    entry.band.focus({preventScroll: true});
  }

  function hideShellElements(entry) {
    rescueShellFocus(entry);
    shellElements().forEach((element) => {
      if (!shellElementState.has(element)) {
        shellElementState.set(element, {hadInert: element.hasAttribute('inert')});
      }
      element.setAttribute('inert', '');
    });
  }

  function restoreShellElements() {
    shellElementState.forEach((state, element) => {
      if (!state.hadInert) element.removeAttribute('inert');
    });
    shellElementState.clear();
  }

  function applyShellTakeover(entry) {
    if (entry.shellTakeoverActive) return;
    shellOwners.add(entry);
    entry.shellTakeoverActive = true;
    hideShellElements(entry);
    document.documentElement.dataset.evaShellTakeover = 'active';
  }

  function releaseShellTakeover(entry) {
    if (!entry.shellTakeoverActive) return;
    shellOwners.delete(entry);
    entry.shellTakeoverActive = false;
    if (shellOwners.size) {
      hideShellElements(shellOwners.values().next().value);
      return;
    }
    document.documentElement.removeAttribute('data-eva-shell-takeover');
    restoreShellElements();
  }

  function syncShellTakeover(entry, first, last) {
    if (!entry.shellTakeoverEnabled || entry.band.dataset.evaStep !== 'ready' || !first || !last) {
      releaseShellTakeover(entry);
      return;
    }
    const y = window.scrollY;
    const active = entry.shellTakeoverActive
      ? y >= first.y - SHELL_TAKEOVER_HYSTERESIS && y <= last.y + SHELL_TAKEOVER_HYSTERESIS
      : y >= first.y + SHELL_TAKEOVER_HYSTERESIS && y <= last.y - SHELL_TAKEOVER_HYSTERESIS;
    if (active) applyShellTakeover(entry);
    else releaseShellTakeover(entry);
  }

  function syncShellReady() {
    document.documentElement.toggleAttribute(
      'data-eva-shell-takeover-ready',
      bands.some((entry) => entry.shellTakeoverEnabled && entry.band.dataset.evaStep === 'ready')
    );
  }

  function readMotion(band) {
    const value = Number.parseFloat(
      getComputedStyle(band).getPropertyValue('--eva-band-step-duration')
    );
    return {duration: Number.isFinite(value) && value > 0 ? value : DEFAULTS.duration};
  }

  function measure(entry) {
    if (!entry.geometryDirty && entry.measured) return entry.stops;
    entry.geometryDirty = false;
    entry.measured = true;

    const plan = [];
    entry.band.querySelectorAll('[data-eva-chapter]').forEach((chapter) => {
      const beats = Math.max(1, Number.parseInt(chapter.dataset.evaBeats ?? '1', 10) || 1);
      for (let beat = 0; beat < beats; beat += 1) plan.push({chapter, beat});
    });

    const bandTop = entry.band.getBoundingClientRect().top + window.scrollY;
    const stageHeight = entry.band.querySelector('[data-eva-band-deck]')?.offsetHeight ?? window.innerHeight;
    const step = plan.length
      ? (entry.band.dataset.evaStep === 'ready' && plan.length > 1
        ? (entry.band.offsetHeight - stageHeight) / (plan.length - 1)
        : entry.band.offsetHeight / plan.length)
      : 0;
    entry.bandTop = bandTop;
    entry.step = step;
    entry.stopCount = plan.length;
    entry.stops = plan.length >= 2 && step > 1
      ? plan.map((slot, index) => ({index, y: Math.round(bandTop + index * step), chapter: slot.chapter, beat: slot.beat}))
      : [];
    return entry.stops;
  }

  function nearestStop(entry, y = window.scrollY) {
    const measured = measure(entry);
    if (!measured.length) return null;
    const raw = Math.round((y - entry.bandTop) / entry.step);
    return measured[Math.min(measured.length - 1, Math.max(0, raw))];
  }

  function beatElements(entry, stop) {
    if (!stop) return [];
    return [...stop.chapter.querySelectorAll(`[data-eva-beat="${stop.beat}"]`)];
  }

  function clearExit(entry) {
    clearTimeout(entry.exitTimer);
    entry.exitTimer = 0;
    (entry.exitElements ?? []).forEach((element) => element.classList.remove('is-eva-leaving'));
    entry.exitElements = [];
    entry.exitChapter?.classList.remove('is-eva-outgoing');
    entry.exitChapter = null;
  }

  function beginExit(entry, previous, next) {
    clearExit(entry);
    entry.exitElements = beatElements(entry, previous);
    entry.exitElements.forEach((element) => element.classList.add('is-eva-leaving'));
    if (previous.chapter !== next.chapter) {
      previous.chapter.classList.add('is-eva-outgoing');
      entry.exitChapter = previous.chapter;
    }
    entry.exitTimer = window.setTimeout(
      () => clearExit(entry),
      entry.motion?.duration ?? DEFAULTS.duration
    );
  }

  function rescueFocus(entry, stop) {
    const focused = document.activeElement;
    if (!focused || !entry.band.contains(focused)) return;
    const owner = focused.closest('[data-eva-chapter]');
    if (!owner || owner === stop.chapter) return;
    stop.chapter.setAttribute('tabindex', '-1');
    stop.chapter.focus({preventScroll: true});
  }

  function setCurrentChapter(entry, chapter) {
    entry.band.querySelectorAll('.is-eva-current').forEach((element) => {
      if (element !== chapter) element.classList.remove('is-eva-current');
    });
    chapter?.classList.add('is-eva-current');
  }

  function syncCharacter(entry) {
    if (entry.band.dataset.evaStep !== 'ready' || !entry.character) return;
    const state = window.scrollY >= entry.bandTop ? 'entered' : 'before';
    if (entry.characterState === state) return;
    const previous = entry.characterState;
    entry.characterState = state;
    entry.band.dataset.evaCharacterState = state;
    if (previous === null) {
      entry.characterSweepArmed = Boolean(entry.characterSweep && state === 'before');
      return;
    }
    clearTimeout(entry.characterMotionTimer);
    const playSweep = state === 'entered' && entry.characterSweepArmed;
    entry.band.dataset.evaCharacterMotion = state === 'entered'
      ? (playSweep ? 'enter' : 'enter-suppressed')
      : 'exit';
    if (state === 'entered') entry.characterSweepArmed = false;
    entry.characterMotionTimer = window.setTimeout(() => {
      entry.characterMotionTimer = 0;
      if (state === 'before' && entry.characterState === 'before') entry.characterSweepArmed = Boolean(entry.characterSweep);
      entry.band.removeAttribute('data-eva-character-motion');
    }, state === 'entered' ? 540 : 240);
  }

  function primeCharacter(entry) {
    if (!entry.character || entry.band.dataset.evaStep !== 'ready') return;
    cancelAnimationFrame(entry.characterPrimeFrame);
    entry.band.setAttribute('data-eva-character-initializing', '');
    syncCharacter(entry);
    void entry.character.offsetWidth;
    entry.characterPrimeFrame = requestAnimationFrame(() => {
      entry.characterPrimeFrame = 0;
      entry.band.removeAttribute('data-eva-character-initializing');
    });
  }

  function setActive(entry, index) {
    const measured = measure(entry);
    const stop = measured[index];
    if (!stop) return;
    if (index === entry.activeStop && beatElements(entry, stop).some((el) => el.classList.contains('is-eva-active'))) return;
    const previous = entry.activeStop !== null && entry.activeStop !== index
      ? measured[entry.activeStop]
      : null;
    if (previous) {

      const direction = index > entry.activeStop ? 'forward' : 'backward';
      if (entry.band.dataset.evaDirection !== direction) {
        entry.band.dataset.evaDirection = direction;
        void entry.band.offsetHeight;
      }
    }
    entry.band.querySelectorAll('.is-eva-active').forEach((element) => element.classList.remove('is-eva-active'));
    if (previous) beginExit(entry, previous, stop);
    setCurrentChapter(entry, stop.chapter);
    beatElements(entry, stop).forEach((element) => element.classList.add('is-eva-active'));
    if (previous) rescueFocus(entry, stop);
    entry.activeStop = index;
    entry.band.dataset.evaActiveStop = String(index);
  }

  function syncActive(entry) {
    if (entry.measured) {
      const liveBandTop = entry.band.getBoundingClientRect().top + window.scrollY;
      if (Math.abs(liveBandTop - entry.bandTop) > 1) entry.geometryDirty = true;
    }
    const measured = measure(entry);
    if (!measured.length) {
      releaseShellTakeover(entry);
      return;
    }
    syncCharacter(entry);
    const first = measured[0];
    const last = measured.at(-1);
    syncShellTakeover(entry, first, last);
    if (window.scrollY < first.y - window.innerHeight || window.scrollY > last.y + window.innerHeight) {
      clearExit(entry);
      entry.band.removeAttribute('data-eva-active-stop');
      entry.band.removeAttribute('data-eva-direction');
      entry.band.querySelectorAll('.is-eva-active').forEach((element) => element.classList.remove('is-eva-active'));
      entry.band.querySelectorAll('.is-eva-current').forEach((element) => element.classList.remove('is-eva-current'));
      entry.activeStop = null;
      return;
    }
    let index = nearestStop(entry)?.index ?? 0;
    if (entry.activeStop !== null && index !== entry.activeStop) {
      const raw = (window.scrollY - entry.bandTop) / entry.step;
      if (Math.abs(raw - entry.activeStop) < 0.5 + HYSTERESIS) index = entry.activeStop;
    }
    setActive(entry, index);
  }

  function syncAll() {
    bands.forEach((entry) => syncActive(entry));
  }

  function onScroll() {
    if (activationFrame) return;
    activationFrame = requestAnimationFrame(() => {
      activationFrame = 0;
      syncAll();
    });
  }

  function invalidateGeometry() {
    bands.forEach((entry) => {
      entry.geometryDirty = true;
      entry.measured = false;
    });
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      refresh();
    });
  }

  function observeContentGeometry() {
    if (!('ResizeObserver' in window)) return;
    geometryObserver ??= new ResizeObserver(invalidateGeometry);
    bands.forEach((entry) => {
      entry.band.querySelectorAll('[data-eva-anim="copy"]').forEach((element) => {
        if (observedGeometry.has(element)) return;
        observedGeometry.add(element);
        geometryObserver.observe(element);
      });
    });
  }

  function disableEntry(entry) {
    releaseShellTakeover(entry);
    clearExit(entry);
    cancelAnimationFrame(entry.characterPrimeFrame);
    clearTimeout(entry.characterMotionTimer);
    entry.band.removeAttribute('data-eva-step');
    entry.band.removeAttribute('data-eva-active-stop');
    entry.band.removeAttribute('data-eva-direction');
    entry.band.removeAttribute('data-eva-character-state');
    entry.band.removeAttribute('data-eva-character-motion');
    entry.band.removeAttribute('data-eva-character-initializing');
    entry.band.querySelectorAll('.is-eva-active, .is-eva-current, .is-eva-outgoing').forEach((element) => {
      element.classList.remove('is-eva-active', 'is-eva-current', 'is-eva-outgoing');
    });
    entry.activeStop = null;
    entry.characterState = null;
    entry.characterSweepArmed = false;
    entry.characterPrimeFrame = 0;
    entry.characterMotionTimer = 0;
    syncTrack(entry);
  }

  function syncTrack(entry) {
    const track = entry.band.querySelector('[data-eva-band-track]');
    if (!track) return;
    const need = entry.band.dataset.evaStep === 'ready' ? Math.max(0, entry.stopCount - 1) : 0;
    while (track.childElementCount > need) track.lastElementChild.remove();
    while (track.childElementCount < need) {
      const spacer = document.createElement('div');
      spacer.className = 'eva-feature-band__stop';
      spacer.setAttribute('data-eva-band-stop', '');
      track.appendChild(spacer);
    }
  }

  function refresh() {
    bands.forEach((entry) => disableEntry(entry));
    bands = [...document.querySelectorAll('[data-eva-band][data-eva-step-enabled]')].map((band) => ({
      band,
      stops: [],
      measured: false,
      geometryDirty: true,
      activeStop: null,
      motion: DEFAULTS,
      exitElements: [],
      exitChapter: null,
      exitTimer: 0,
      character: band.hasAttribute('data-eva-character-entrance')
        ? band.querySelector('[data-eva-band-character-shell]')
        : null,
      characterSweep: band.querySelector('[data-eva-band-character-sweep]'),
      characterState: null,
      characterSweepArmed: false,
      characterPrimeFrame: 0,
      characterMotionTimer: 0,
      shellTakeoverEnabled: band.hasAttribute('data-eva-shell-takeover-enabled'),
      shellTakeoverActive: false,
      bandTop: 0,
      step: 0,
      stopCount: 0,
    }));
    const deckCapable = deckQuery.matches && !window.Shopify?.designMode;

    const plans = bands.map((entry) => {
      entry.motion = readMotion(entry.band);
      const coherent = measure(entry).length >= 2;

      const deckSafe = coherent
        && entry.step >= window.innerHeight * 0.4
        && entry.step <= window.innerHeight + 8;
      return {entry, coherent, deckSafe};
    });
    plans.forEach(({entry, coherent, deckSafe}) => {
      if (deckCapable && coherent && deckSafe) {
        entry.band.dataset.evaStep = 'ready';
        primeCharacter(entry);
      }
      else disableEntry(entry);
      syncTrack(entry);
    });
    syncShellReady();
    observeContentGeometry();
    syncAll();
  }

  const readyHandler = () => refresh();
  document.addEventListener('DOMContentLoaded', readyHandler);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', refresh);
  document.addEventListener('shopify:section:select', refresh);
  window.addEventListener('resize', invalidateGeometry);
  window.addEventListener('scroll', onScroll, {passive: true});
  deckQuery.addEventListener('change', refresh);
  window.visualViewport?.addEventListener('resize', invalidateGeometry);
  if (document.readyState !== 'loading') refresh();
  document.fonts?.ready.then(() => {
    if (window[CONTROLLER_KEY]?.version === VERSION) invalidateGeometry();
  });

  window[CONTROLLER_KEY] = {
    version: VERSION,
    refresh,
    destroy() {
      cancelAnimationFrame(activationFrame);
      cancelAnimationFrame(resizeFrame);
      geometryObserver?.disconnect();
      geometryObserver = null;
      bands.forEach((entry) => disableEntry(entry));
      bands = [];
      document.removeEventListener('DOMContentLoaded', readyHandler);
      document.removeEventListener('shopify:section:load', refresh);
      document.removeEventListener('shopify:section:unload', refresh);
      document.removeEventListener('shopify:section:select', refresh);
      window.removeEventListener('resize', invalidateGeometry);
      window.removeEventListener('scroll', onScroll, {passive: true});
      deckQuery.removeEventListener('change', refresh);
      window.visualViewport?.removeEventListener('resize', invalidateGeometry);
      document.documentElement.removeAttribute('data-eva-shell-takeover-ready');
      document.documentElement.removeAttribute('data-eva-shell-takeover');
      restoreShellElements();
      delete window[CONTROLLER_KEY];
    },
    getState() {
      const enabled = bands.filter((entry) => entry.band.dataset.evaStep === 'ready');
      const first = enabled[0] ?? bands[0] ?? null;
      return {
        activeStop: first?.activeStop ?? null,
        characterState: first?.characterState ?? null,
        shellTakeoverActive: first?.shellTakeoverActive ?? false,
        shellTakeoverOwnerCount: shellOwners.size,
        enabled: enabled.length > 0,
        state: 'idle',
        stops: first ? measure(first).map(({index, y}) => ({index, y})) : [],
        bands: bands.map((entry) => ({
          enabled: entry.band.dataset.evaStep === 'ready',
          activeStop: entry.activeStop,
          characterState: entry.characterState,
          shellTakeoverActive: entry.shellTakeoverActive,
          stops: measure(entry).map(({index, y}) => ({index, y})),
        })),
      };
    },
  };
})();
